import { Button, Collapse, DatePicker, Form, FormInstance, Radio, Select, Space } from 'antd';
import React, { useState, useCallback, useEffect } from 'react';
import { CompanyInterface } from 'type/companyType';
import { SeasonInterface } from 'type/seasonType';
import { companyApiService } from 'service/api/companyApiService';
import { seasonService } from 'content/service/seasonService';
import { debounce } from 'lodash';

const { Option } = Select;

type RevenueFilterProps = {
  form: FormInstance;
  searchParams: URLSearchParams;
  handleFilterValueChange: (
    value: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)
  ) => void;
  handleFilterSubmit: () => void;
  handleFilterReset: () => void;
};
export const RevenueFilter: React.FC<RevenueFilterProps> = ({
  form,
  handleFilterValueChange,
  handleFilterSubmit,
  handleFilterReset,
}: RevenueFilterProps) => {
  const [searchCompanies, setSearchCompanies] = useState<CompanyInterface[]>([]);
  const [defaultCompanies, setDefaultCompanies] = useState<CompanyInterface[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [searchSeasons, setSearchSeasons] = useState<SeasonInterface[]>([]);
  const [defaultSeasons, setDefaultSeasons] = useState<SeasonInterface[]>([]);
  const [seasonSearchLoading, setSeasonSearchLoading] = useState(false);

  // Load default company list (100 items)
  useEffect(() => {
    const loadDefaultCompanies = async () => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page_size', '100');

        const result = await companyApiService.fetch(queryParams);

        setDefaultCompanies(result.results);
        setSearchCompanies(result.results); // Initially set default list as search results too
      } catch (error) {
        console.error('❌ [Revenue Dashboard] Failed to load default companies:', error);
      }
    };

    loadDefaultCompanies();
  }, []);

  // Load default season list (100 items)
  useEffect(() => {
    const loadDefaultSeasons = async () => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page_size', '100');

        const result = await seasonService.fetch(queryParams);

        setDefaultSeasons(result.results);
        setSearchSeasons(result.results); // Initially set default list as search results too
      } catch (error) {
        console.error('❌ [Revenue Dashboard] Failed to load default seasons:', error);
      }
    };

    loadDefaultSeasons();
  }, []);

  // Season search function (with debounce)
  const debouncedSearchSeasons = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        // Show default list when search term is empty
        setSearchSeasons(defaultSeasons);
        return;
      }

      setSeasonSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await seasonService.fetch(queryParams);
        setSearchSeasons(result.results);
      } catch (error) {
        console.error('Season search failed:', error);
        setSearchSeasons([]);
      } finally {
        setSeasonSearchLoading(false);
      }
    }, 300),
    [defaultSeasons]
  );

  // Restore default list when Season Select box opens
  const handleSeasonDropdownVisibleChange = (open: boolean) => {
    if (open) {
      // Show default list each time Select box opens
      setSearchSeasons(defaultSeasons);
    }
  };

  // Handle Season search term change
  const handleSeasonSearch = (value: string) => {
    if (!value.trim()) {
      // Show default list immediately when search term is empty
      setSearchSeasons(defaultSeasons);
    } else {
      debouncedSearchSeasons(value);
    }
  };

  // Handle when search term is cleared via Season X button
  const handleSeasonClear = () => {
    setSearchSeasons(defaultSeasons);
  };

  // Company search function (with debounce)
  const debouncedSearchCompanies = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        // Show default list when search term is empty
        setSearchCompanies(defaultCompanies);
        return;
      }

      setSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await companyApiService.fetch(queryParams);
        setSearchCompanies(result.results);
      } catch (error) {
        console.error('Company search failed:', error);
        setSearchCompanies([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    [defaultCompanies]
  );

  // Restore default list when Select box opens
  const handleDropdownVisibleChange = (open: boolean) => {
    if (open) {
      // Show default list each time Select box opens
      setSearchCompanies(defaultCompanies);
    }
  };

  // Handle search term change
  const handleSearch = (value: string) => {
    if (!value.trim()) {
      // Show default list immediately when search term is empty
      setSearchCompanies(defaultCompanies);
    } else {
      debouncedSearchCompanies(value);
    }
  };

  // Handle when search term is cleared via X button
  const handleClear = () => {
    setSearchCompanies(defaultCompanies);
  };

  // Company option rendering function
  const renderCompanyOption = (company: CompanyInterface) => {
    const titlePart = company.title_en ? `${company.title} (${company.title_en})` : company.title;
    const countryPart = company.country ? ` / ${company.country}` : '';
    const label = titlePart + countryPart;

    return (
      <Option key={company.id} value={company.id.toString()} title={label}>
        <span style={{ fontWeight: 500 }}>{label}</span>
      </Option>
    );
  };

  // Season option rendering function
  const renderSeasonOption = (season: SeasonInterface) => {
    const label = season.title;

    return (
      <Option key={season.id} value={season.id.toString()} title={label}>
        <span style={{ fontWeight: 500 }}>{label}</span>
      </Option>
    );
  };

  return (
    <Collapse
      defaultActiveKey={['1']}
      style={{ marginBottom: 20 }}
      items={[
        {
          key: '1',
          label: 'Revenue Filter',
          children: (
            <Form form={form} layout="horizontal" onFinish={handleFilterSubmit}>
              <div>
                <Form.Item name="start_yearmon" label="Start Date">
                  <DatePicker
                    picker="month"
                    style={{ width: '10%' }}
                    placeholder="Select start date"
                    format="YYYY-MM"
                    onChange={e =>
                      handleFilterValueChange((prev: Record<string, string>) => ({
                        ...prev,
                        start_yearmon: e.format('YYYY-MM'),
                      }))
                    }
                  />
                </Form.Item>
                <Form.Item name="end_yearmon" label="End Date">
                  <DatePicker
                    picker="month"
                    style={{ width: '10%' }}
                    placeholder="Select end date"
                    format="YYYY-MM"
                    onChange={e =>
                      handleFilterValueChange(prev => ({
                        ...prev,
                        end_yearmon: e ? e.format('YYYY-MM') : '',
                      }))
                    }
                  />
                </Form.Item>
              </div>
              <Form.Item
                name="content_provider_ids"
                label="Content Provider"
                style={{ width: '50%' }}
              >
                <Select
                  allowClear
                  placeholder="Search content provider..."
                  mode="multiple"
                  showSearch
                  filterOption={false}
                  onSearch={handleSearch}
                  onClear={handleClear}
                  loading={searchLoading}
                  notFoundContent={searchLoading ? 'Searching...' : 'No companies found'}
                  onSelect={value =>
                    handleFilterValueChange(prev => {
                      const currentIds = prev.content_provider_ids
                        ? prev.content_provider_ids.split(',').filter(Boolean)
                        : [];
                      const newIds = [...new Set([...currentIds, value])];
                      return {
                        ...prev,
                        content_provider_ids: newIds.join(','),
                      };
                    })
                  }
                  onDeselect={value =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      content_provider_ids: prev.content_provider_ids
                        .split(',')
                        .filter(Boolean)
                        .filter(id => id !== value)
                        .join(','),
                    }))
                  }
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  onDropdownVisibleChange={handleDropdownVisibleChange}
                >
                  {searchCompanies.map(renderCompanyOption)}
                </Select>
              </Form.Item>

              <Form.Item name="season_ids" label="Season" style={{ width: '40%' }}>
                <Select
                  allowClear
                  mode="multiple"
                  showSearch
                  placeholder="Search season..."
                  filterOption={false}
                  onSearch={handleSeasonSearch}
                  onClear={handleSeasonClear}
                  loading={seasonSearchLoading}
                  notFoundContent={seasonSearchLoading ? 'Searching...' : 'No seasons found'}
                  onSelect={value =>
                    handleFilterValueChange(prev => {
                      const currentIds = prev.season_ids
                        ? prev.season_ids.split(',').filter(Boolean)
                        : [];
                      const newIds = [...new Set([...currentIds, value])];
                      return {
                        ...prev,
                        season_ids: newIds.join(','),
                      };
                    })
                  }
                  onDeselect={value =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      season_ids: prev.season_ids
                        .split(',')
                        .filter(Boolean)
                        .filter(id => id !== value)
                        .join(','),
                    }))
                  }
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  onDropdownVisibleChange={handleSeasonDropdownVisibleChange}
                >
                  {searchSeasons.map(renderSeasonOption)}
                </Select>
              </Form.Item>

              <Form.Item name="group_by" label="Group By">
                <Radio.Group
                  style={{ marginBottom: 20, fontSize: 16 }}
                  onChange={e =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      group_by: e.target.value,
                    }))
                  }
                >
                  Group By:
                  <Radio style={{ marginLeft: 10 }} value="yearmon">
                    Year Month
                  </Radio>
                  <Radio style={{ marginLeft: 10 }} value="yearmon_content_provider">
                    Year Month Content Provider
                  </Radio>
                  <Radio style={{ marginLeft: 10 }} value="yearmon_season">
                    Year Month Season
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    Apply Filters
                  </Button>
                  <Button onClick={handleFilterReset}>Reset</Button>
                </Space>
              </Form.Item>
            </Form>
          ),
        },
      ]}
    />
  );
};
